import * as React from "react";
import {RouteComponentProps, withRouter} from "react-router";
import {LeadEventType} from "../../model/lead/event/LeadEvent";
import Note from "../../model/note/Note";
import Appointment from "../../model/appointment/Appointment";
import MultiSelectDropDown from "../../hiberUiKit/src/molecules/MultiSelectDropDown/MultiSelectDropDown";
import LeadEventTypeObject, {LeadEventCategory} from "../../model/lead/event/LeadEventTypeObject";
import LocalStorage from "../../store/local/LocalStorage";
import LeadEventCategoryType from "../../model/lead/event/LeadEventCategoryType";
import ActivityStreamUtils from "../../utils/ActivityStreamUtils";
import LeadActivityStreamItems from "./LeadActivityStreamItems";
import deepEqual = require("deep-equal");

export type LeadActivityItemsType = LeadEventType | Note;

interface ComponentProps {
	/**
	 * Lead notes.
	 */
	notes: Note[]
	/**
	 * Lead events.
	 */
	leadEvents: Array<LeadEventType>
	/**
	 * Note id to set active class and focus on it.
	 */
	activeNoteId: string;
	/**
	 * Edit note click action.
	 * @param note note to edit.
	 */
	onEditNoteClick: (note: Note) => void;
	/**
	 * Lead appointments.
	 */
	appointments: Appointment[];
	/**
	 * Edit appointment click action.
	 * @param appointment to edit.
	 */
	onEditAppointmentClick: (appointmentId: string) => void;
}

interface RouteProps {
	leadId: string
}

interface State {
	/**
	 * Lead activity data.
	 */
	activityData: LeadActivityItemsType[]
	/**
	 * Filtered activity data
	 */
	filteredActivityData: LeadActivityItemsType[]
	/**
	 * Selected filters
	 */
	selectedFiltersIds: string[]
}

type Props = ComponentProps & RouteComponentProps<RouteProps>;

/**
 * Lead statistics component.
 */
class LeadActivityStream extends React.PureComponent<Props, State> {
	private filterList: LeadEventCategoryType[] = LeadEventCategoryType.getAllCategories();

	constructor(props: Props) {
		super(props);
		const selectedFiltersIds = LocalStorage.getLeadActivityFilterIds();
		const activityData = this.mergeActivityStreamItems(props.leadEvents, props.notes);
		const filteredActivityData = this.filterActivityData(activityData, selectedFiltersIds);
		this.state = {activityData, filteredActivityData, selectedFiltersIds};
		this.updateFilter = this.updateFilter.bind(this)
	}

	componentWillReceiveProps(newProps: Props) {
		if (!deepEqual(newProps.notes, this.props.notes) || !deepEqual(newProps.leadEvents, this.props.leadEvents)) {
			const activityData = this.mergeActivityStreamItems(newProps.leadEvents, newProps.notes);
			const filteredActivityData = this.filterActivityData(activityData, this.state.selectedFiltersIds);
			this.setState({activityData, filteredActivityData})
		}
	}

	componentWillUnmount() {
		this.filterList = null;
	}

	render() {
		return (
			<>
				<div className={"l-lead-details-filter-container"}>
					<MultiSelectDropDown items={this.filterList}
										 itemToId={(item: LeadEventCategoryType) => item.id}
										 itemToLabel={(item: LeadEventCategoryType) => item.getLabel()}
										 selectedItemIds={this.state.selectedFiltersIds}
										 onSelect={this.updateFilter}/>
				</div>
				<div className={this.state.filteredActivityData.length === 1 ? "is-single" : ""}>
					<LeadActivityStreamItems activityItems={this.state.filteredActivityData}
											 activeNoteId={this.props.activeNoteId}
											 onEditAppointmentClick={this.props.onEditAppointmentClick}
											 onEditNoteClick={this.props.onEditNoteClick}/>
				</div>
			</>
		)
	}

	/**
	 * Update filter and save filter in local storage
	 * @param {LeadEventTypeObject[]} filters
	 */
	updateFilter(filters: LeadEventTypeObject[]) {
		const selectedFiltersIds = filters.map(t => t.id);
		const filteredActivityData = this.filterActivityData(this.state.activityData, selectedFiltersIds);
		LocalStorage.saveLeadActivityFilterIds(selectedFiltersIds);

		this.setState({selectedFiltersIds, filteredActivityData})
	}

	/**
	 * Filter activity data by selected filters
	 * @param {LeadActivityItemsType[]} activityData
	 * @param {string[]} filters
	 * @returns {LeadActivityItemsType[]}
	 */
	filterActivityData(activityData: LeadActivityItemsType[], filters: string[]): LeadActivityItemsType[] {
		let filteredData: LeadActivityItemsType[] = [];
		filters.forEach(filter => filteredData = filteredData.concat(this.filterSingleCategory(filter, activityData)));
		return filteredData.sort(ActivityStreamUtils.sort);
	}

	/**
	 * Filter single category.
	 * @param {string} filter key of the category.
	 * @param {LeadActivityItemsType[]} activityData All data to filter.
	 * @return {LeadActivityItemsType[]} Filtered data by key.
	 */
	filterSingleCategory(filter: string, activityData: LeadActivityItemsType[]) {
		if (filter === LeadEventCategory.Notes) {
			return activityData.filter(ad => ad instanceof Note && ad.reminderDate.isEmpty);
		}
		if (filter === LeadEventCategory.Reminders) {
			return activityData.filter(ad => ad instanceof Note && ad.reminderDate.isDefined);
		}
		else {
			return activityData.filter(ad =>
				!(ad instanceof Note) &&
				LeadEventTypeObject.getTypesForCategory(filter).map((lto: LeadEventTypeObject) => lto.id).indexOf(ad.type) > -1);
		}
	}

	/**
	 * Merge activity stream data and sort it by date - latest first.
	 * @param {LeadEventType[]} events lead events.
	 * @param {Note[]} notes lead notes.
	 * @return {LeadActivityItemsType[]}
	 */
	mergeActivityStreamItems(events: Array<LeadEventType>, notes: Note[]): Array<LeadActivityItemsType> {
		return [].concat(events).concat(notes).sort(ActivityStreamUtils.sort);
	}
}

export default withRouter<ComponentProps>(LeadActivityStream);